/**
 * Contextual help side-drawer — R-131-SUP-C
 */
// R-131-SUP-C
import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, X } from 'lucide-react';
import type { HelpSlug } from '../../lib/helpArticles';
import { HELP_ARTICLE_BY_SLUG, helpArticlePath } from '../../lib/helpArticles';
import { HelpMarkdown } from '../../lib/helpMarkdown';

interface Props {
  slug: HelpSlug | null;
  onClose: () => void;
}

export function HelpArticleDrawer({ slug, onClose }: Props): React.ReactElement | null {
  if (!slug) return null;

  const article = HELP_ARTICLE_BY_SLUG[slug];

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-drawer-title"
      data-testid="help-article-drawer"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-canvas/40"
        aria-label="Close help"
        data-testid="help-article-drawer-backdrop"
      />
      <div className="relative bg-surface-1 shadow-xl w-full max-w-lg h-full flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
          <div className="min-w-0">
            <h2 id="help-drawer-title" className="text-sm font-bold text-text-primary truncate">
              {article.title}
            </h2>
            <p className="text-[10px] text-text-muted uppercase tracking-widest mt-0.5">
              {article.section}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to={helpArticlePath(slug)}
              onClick={onClose}
              className="p-1.5 rounded hover:bg-surface-3 text-text-secondary hover:text-text-primary"
              title="Open full page"
              aria-label="Open full help page"
              data-testid="help-drawer-open-full"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-surface-3"
              aria-label="Close"
              data-testid="help-article-drawer-close"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4" data-testid="help-drawer-body">
          <HelpMarkdown>{article.body}</HelpMarkdown>
        </div>
      </div>
    </div>
  );
}
